/**
 * Chop candidate scorer — Phase 2a of the Slicer overhaul.
 *
 * Given a raw onset envelope + a target slice count, picks the N
 * chop points that look *musically* best. The existing
 * `findOnsetPeaks` surfaces every local maximum above a threshold;
 * Smart-Chop layers a scoring pass on top so the user ends up with
 * chop points placed on the strongest, most-evenly-spaced, and
 * (when BPM is known) beat-aligned transients rather than a raw
 * top-N by amplitude.
 *
 * Scoring dimensions (all normalised to 0..1, blended with the
 * weights in `DEFAULT_WEIGHTS`):
 *
 *  1. **Transient strength** — the peak's onset-envelope amplitude
 *     relative to the envelope's max. Bias toward sharp, clean
 *     onsets over soft energy ramps.
 *
 *  2. **Silence gap to next candidate** — candidates packed within a
 *     few dozen milliseconds are almost always the same event (a
 *     ringing kick tail, a flam). Reward candidates with room to
 *     breathe.
 *
 *  3. **Bar / beat alignment** — when a source BPM is known and
 *     "snap" is on, score each candidate by how close it falls to
 *     the nearest 16th-note tick. Weight is low by default — this
 *     is a preference, not a hard requirement, since jungle chops
 *     intentionally land off-grid.
 *
 *  4. **Clustering penalty** — once we've picked a candidate, nearby
 *     neighbours get a soft suppression so the final selection
 *     doesn't cluster in one half of the break. Enforced in a
 *     greedy second pass rather than via pairwise scoring.
 *
 * Pure / no Web Audio dependency — suitable to run in a Worker once
 * the Slicer starts offloading its DSP.
 */

/** A candidate chop point, in *seconds* into the source buffer. */
export interface ChopCandidate {
  /** Time into the source in seconds. */
  timeSec: number
  /** Onset envelope amplitude at this candidate (0..∞). Used as the
   *  raw signal for the "transient strength" score — callers can
   *  pass peak heights direct from `findOnsetPeaks` (via the
   *  envelope). */
  strength: number
}

export interface ScoredCandidate extends ChopCandidate {
  /** Final blended score in 0..1. Higher = better pick. */
  score: number
  /** Per-dimension components, exposed for debugging + UI tooltips. */
  components: {
    strength: number
    gap: number
    alignment: number
  }
}

export interface ScoreOptions {
  /** Total sample duration in seconds. Used to normalise gap /
   *  alignment computations when candidates stretch near the end. */
  durationSec: number
  /** Source tempo. Enables the bar-alignment score. `undefined`
   *  skips that dimension entirely and rescales the remaining
   *  weights. */
  sourceBpm?: number
  /** Subdivision for alignment snap. 4 = quarter notes, 8 = 8ths,
   *  16 = 16ths. Default 16 — that's the jungle chop grid. */
  subdivision?: 4 | 8 | 16
  /** Component weights (override defaults). */
  weights?: Partial<typeof DEFAULT_WEIGHTS>
  /** Horizon for the gap score in seconds — candidates within this
   *  window compete. Default 0.08 (80 ms, ~a fast 32nd at 140 BPM).
   *  Gaps >= horizon get full credit; shorter gaps scale linearly. */
  gapHorizonSec?: number
}

export const DEFAULT_WEIGHTS = {
  strength: 0.5,
  gap: 0.3,
  alignment: 0.2,
}

/**
 * Score every candidate. Output is the input list (sorted by
 * `timeSec` ascending) annotated with scores + component
 * breakdowns. Caller uses `pickTopN` to select N chops.
 */
export function scoreCandidates(
  candidates: ChopCandidate[],
  opts: ScoreOptions,
): ScoredCandidate[] {
  if (candidates.length === 0) return []
  const sorted = [...candidates].sort((a, b) => a.timeSec - b.timeSec)

  // Normalise strength to 0..1 by peak.
  let maxStrength = 0
  for (const c of sorted) {
    if (c.strength > maxStrength) maxStrength = c.strength
  }
  const strengthNorm =
    maxStrength > 0
      ? (v: number) => Math.max(0, Math.min(1, v / maxStrength))
      : () => 0

  const gapHorizon = opts.gapHorizonSec ?? 0.08

  // Weights — drop the alignment component when BPM is unknown and
  // rescale so the remaining weights still sum to 1.
  const w = { ...DEFAULT_WEIGHTS, ...(opts.weights ?? {}) }
  if (!opts.sourceBpm || opts.sourceBpm <= 0) {
    const total = w.strength + w.gap
    w.alignment = 0
    if (total > 0) {
      w.strength /= total
      w.gap /= total
    }
  }

  const tickSec =
    opts.sourceBpm && opts.sourceBpm > 0
      ? 60 / opts.sourceBpm / ((opts.subdivision ?? 16) / 4)
      : 0

  const out: ScoredCandidate[] = sorted.map((c, i) => {
    // Gap: min(distance to prev, distance to next). Normalise by
    // horizon so a 20 ms gap is heavily penalised and a 200 ms gap
    // is treated the same as a 5 s gap.
    const prev = i > 0 ? c.timeSec - sorted[i - 1].timeSec : gapHorizon
    const next =
      i < sorted.length - 1
        ? sorted[i + 1].timeSec - c.timeSec
        : Math.max(gapHorizon, opts.durationSec - c.timeSec)
    const minGap = Math.min(prev, next)
    const gap = Math.max(0, Math.min(1, minGap / gapHorizon))

    // Alignment: 1 when candidate lands exactly on a tick, linearly
    // decreasing to 0 at half a tick off. Zero when BPM unknown.
    let alignment = 0
    if (tickSec > 0) {
      const frac = (c.timeSec / tickSec) % 1
      const dist = Math.min(frac, 1 - frac) // 0..0.5
      alignment = Math.max(0, 1 - dist * 2)
    }

    const strength = strengthNorm(c.strength)
    const score = w.strength * strength + w.gap * gap + w.alignment * alignment

    return {
      ...c,
      score,
      components: { strength, gap, alignment },
    }
  })

  return out
}

/**
 * Greedy selection with a clustering penalty — pick the highest-
 * scoring candidate, zero-out anything within `minSpacingSec` of
 * it, repeat N times (or until candidates run out). Returns picks
 * sorted by time ascending so downstream slice-placement code can
 * consume them directly.
 *
 * Default spacing is 120 ms — roughly a 16th note at 125 BPM.
 * Shorter than that and consecutive picks tend to be the same
 * rhythmic event caught twice.
 */
export function pickTopN(
  scored: ScoredCandidate[],
  count: number,
  minSpacingSec = 0.12,
): ScoredCandidate[] {
  if (scored.length === 0 || count <= 0) return []
  // Copy + sort by score descending for greedy pick.
  const pool = [...scored].sort((a, b) => b.score - a.score)
  const picks: ScoredCandidate[] = []
  const taken = new Set<ScoredCandidate>()

  for (const cand of pool) {
    if (picks.length >= count) break
    if (taken.has(cand)) continue
    let tooClose = false
    for (const p of picks) {
      if (Math.abs(p.timeSec - cand.timeSec) < minSpacingSec) {
        tooClose = true
        break
      }
    }
    if (tooClose) continue
    picks.push(cand)
    taken.add(cand)
  }

  picks.sort((a, b) => a.timeSec - b.timeSec)
  return picks
}

/**
 * Convenience wrapper — take a bare list of onset peak times (in
 * seconds) plus the onset envelope they came from, run the full
 * scorer pipeline, return the top-N picks in time order.
 *
 * `peakTimes` should be the output of `findOnsetPeaks` converted
 * to seconds (frame / frameRate). `envelope` + `frameRate` let us
 * attach a strength value to each time without re-running onset
 * detection.
 */
export function smartChop(
  peakTimes: number[],
  envelope: Float32Array,
  frameRate: number,
  count: number,
  opts: Omit<ScoreOptions, 'durationSec'> & { durationSec: number },
): ScoredCandidate[] {
  if (peakTimes.length === 0) return []
  const candidates: ChopCandidate[] = peakTimes.map(timeSec => {
    const frame = Math.round(timeSec * frameRate)
    const strength = frame >= 0 && frame < envelope.length ? envelope[frame] : 0
    return { timeSec, strength }
  })
  const scored = scoreCandidates(candidates, opts)
  return pickTopN(scored, count)
}
